(function () {
  const CART_UPDATE_EVENT = 'cart:update';
  const CART_ERROR_EVENT = 'cart:error';
  const ADDED_DURATION = 1500;

  // Essential Preorder publishes its campaign config into the DOM on every page
  // via the app embed element. Reading it keeps the bundle rows in sync with
  // whatever campaigns the client runs in the app admin — no theme settings.
  function readPreorderCampaignsByVariant() {
    const map = new Map();
    try {
      const embed = document.querySelector('essential-preorder-app-embed');
      if (!embed) return map;
      const raw = embed.getAttribute('preorder-app-data');
      if (!raw) return map;
      const campaigns = JSON.parse(raw).preorderCampaigns;
      if (!Array.isArray(campaigns)) return map;
      campaigns.forEach((campaign) => {
        const placements = (campaign.placement && campaign.placement.specificProducts) || [];
        placements.forEach((p) => {
          const match = String(p.id || '').match(/ProductVariant\/(\d+)$/);
          if (match) map.set(match[1], campaign);
        });
      });
    } catch (err) {
      /* no campaign data — rows fall back to hiding OOS options */
    }
    return map;
  }

  // A campaign variant is in preorder state when the campaign says OUT_OF_STOCK
  // and the variant actually is, or unconditionally for other campaign types.
  function campaignForOption(map, variantId, isOos) {
    const campaign = map.get(String(variantId));
    if (!campaign) return null;
    const type = campaign.content && campaign.content.preorderType;
    if (type === 'OUT_OF_STOCK' && !isOos) return null;
    return campaign;
  }

  class BundlesBlock extends HTMLElement {
    constructor() {
      super();
      this.tiers = [];
      this.cart = { item_count: 0, items_subtotal_price: 0 };
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    connectedCallback() {
      this.parseConfig();
      this.setTierCount();
      this.bindRows();
      this.fetchCart();
      document.addEventListener(CART_UPDATE_EVENT, this.onCartUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener(CART_UPDATE_EVENT, this.onCartUpdate);
    }

    parseConfig() {
      try {
        const raw = this.getAttribute('data-tiers') || '[]';
        this.tiers = JSON.parse(raw)
          .filter((t) => Number.isFinite(t.min) && Number.isFinite(t.percent))
          .sort((a, b) => a.min - b.min);
      } catch (err) {
        this.tiers = [];
      }
      this.progressTemplate = this.getAttribute('data-progress-template') || '';
      this.reachedTemplate = this.getAttribute('data-reached-template') || '';
      this.topReachedTemplate = this.getAttribute('data-top-reached-template') || '';
    }

    setTierCount() {
      this.style.setProperty('--tier-count', String(this.tiers.length || 1));
    }

    bindRows() {
      this.preorderCampaigns = readPreorderCampaignsByVariant();
      this.rowCampaigns = new WeakMap();

      this.querySelectorAll('[data-bundles-row]').forEach((row) => {
        this.pruneOptions(row);
        this.updatePreorderState(row);
        row.addEventListener('submit', (e) => this.handleSubmit(e, row));

        row.querySelectorAll('[data-qty-step]').forEach((btn) => {
          btn.addEventListener('click', () => {
            const step = parseInt(btn.getAttribute('data-qty-step'), 10) || 1;
            const input = row.querySelector('[data-bundles-qty]');
            if (!input) return;
            const current = parseInt(input.value, 10) || 1;
            const next = Math.max(1, current + step);
            input.value = String(next);
          });
        });

        const sizeSelect = row.querySelector('[data-bundles-size]');
        const priceEl = row.querySelector('[data-bundles-price]');
        if (sizeSelect) {
          sizeSelect.addEventListener('change', () => {
            if (priceEl && !priceEl.hasAttribute('data-from-price')) {
              const opt = sizeSelect.options[sizeSelect.selectedIndex];
              const priceCents = opt && opt.getAttribute('data-price');
              if (priceCents != null) {
                const priceSpan = priceEl.querySelector('.price') || priceEl;
                priceSpan.textContent = formatMoney(parseInt(priceCents, 10));
              }
            }
            this.updatePreorderState(row);
          });
        }
      });
    }

    // Remove options that can never be sold: unavailable variants, and
    // out-of-stock variants with no preorder campaign behind them. If nothing
    // sellable remains, the whole row goes.
    pruneOptions(row) {
      const select = row.querySelector('[data-bundles-size]');

      if (!select) {
        const single = row.querySelector('[data-bundles-single]');
        if (!single) return;
        const isOos = single.hasAttribute('data-oos');
        const unsellable =
          single.hasAttribute('data-unavailable') ||
          (isOos && !campaignForOption(this.preorderCampaigns, single.value, isOos));
        if (unsellable) row.hidden = true;
        return;
      }

      Array.from(select.options).forEach((opt) => {
        const isOos = opt.hasAttribute('data-oos');
        const unsellable =
          opt.hasAttribute('data-unavailable') ||
          (isOos && !campaignForOption(this.preorderCampaigns, opt.value, isOos));
        if (unsellable) opt.remove();
      });

      if (!select.options.length) {
        row.hidden = true;
        return;
      }
      if (select.selectedIndex < 0) select.selectedIndex = 0;
    }

    // Flip the row between normal Add and Preorder based on the selected
    // variant, and remember the matched campaign for handleSubmit.
    updatePreorderState(row) {
      const button = row.querySelector('[data-bundles-add]');
      const label = row.querySelector('.bundles__add-label');
      const note = row.querySelector('[data-bundles-preorder-note]');
      if (!button || !label) return;

      const select = row.querySelector('[data-bundles-size]');
      const input = select || row.querySelector('[data-bundles-single]');
      if (!input) return;

      const opt = select ? select.options[select.selectedIndex] : input;
      if (!opt) return;

      const campaign = campaignForOption(this.preorderCampaigns, opt.value, opt.hasAttribute('data-oos'));
      this.rowCampaigns.set(row, campaign);

      if (!label.hasAttribute('data-default-label')) {
        label.setAttribute('data-default-label', label.textContent.trim());
      }

      if (campaign) {
        const content = campaign.content || {};
        label.textContent = content.buttonText || 'Preorder';
        if (note) {
          note.textContent = content.message || '';
          note.hidden = !note.textContent;
          const color = campaign.design && campaign.design.messageColor;
          if (color) note.style.color = color;
        }
      } else {
        label.textContent = label.getAttribute('data-default-label');
        if (note) {
          note.textContent = '';
          note.hidden = true;
        }
      }
    }

    async fetchCart() {
      try {
        const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const cart = await res.json();
        this.cart = cart;
        this.render();
      } catch (err) {
        /* silent — render will run with default zero state */
      }
    }

    onCartUpdate(e) {
      // Theme dispatches cart:update with detail.resource (the cart) or detail.data
      const detail = e && e.detail;
      const resource = detail && (detail.resource || detail.cart);
      if (resource && typeof resource.item_count === 'number') {
        this.cart = resource;
        this.render();
      } else {
        // Fall back to fetching fresh cart
        this.fetchCart();
      }
    }

    getCurrentTier() {
      let current = null;
      for (const tier of this.tiers) {
        if (this.cart.item_count >= tier.min) current = tier;
      }
      return current;
    }

    getNextTier() {
      for (const tier of this.tiers) {
        if (this.cart.item_count < tier.min) return tier;
      }
      return null;
    }

    render() {
      this.renderTiers();
      this.renderProgress();
      this.renderFooter();
    }

    renderTiers() {
      this.querySelectorAll('[data-tier-min]').forEach((el) => {
        const min = parseInt(el.getAttribute('data-tier-min'), 10);
        el.classList.toggle('is-reached', this.cart.item_count >= min);
      });

      const next = this.getNextTier();
      this.querySelectorAll('[data-tier-min]').forEach((el) => {
        const min = parseInt(el.getAttribute('data-tier-min'), 10);
        el.classList.toggle('is-next', next && next.min === min);
      });
    }

    renderProgress() {
      const progressEl = this.querySelector('[data-bundles-progress]');
      if (!progressEl) return;

      const current = this.getCurrentTier();
      const next = this.getNextTier();

      let html = '';
      if (next) {
        const countToGo = next.min - this.cart.item_count;
        html = fillTemplate(this.progressTemplate, {
          count: '<b>' + countToGo + '</b>',
          percent: '<b>' + next.percent + '</b>',
        });
      } else if (current) {
        html = fillTemplate(this.topReachedTemplate, {
          percent: '<b>' + current.percent + '</b>',
        });
      }

      progressEl.innerHTML = html;
    }

    renderFooter() {
      const footer = this.querySelector('[data-bundles-footer]');
      if (!footer) return;

      if (this.cart.item_count === 0) {
        footer.hidden = true;
        return;
      }

      footer.hidden = false;

      const totalEl = footer.querySelector('[data-bundles-total]');
      if (totalEl) {
        const itemsLabel = this.cart.item_count === 1 ? 'item' : 'items';
        totalEl.textContent =
          this.cart.item_count + ' ' + itemsLabel + ' in cart · ' + formatMoney(this.cart.items_subtotal_price);
      }

      const savingEl = footer.querySelector('[data-bundles-saving]');
      if (savingEl) {
        const tier = this.getCurrentTier();
        if (tier && tier.percent > 0) {
          const saved = Math.round((this.cart.items_subtotal_price * tier.percent) / 100);
          savingEl.textContent = 'Saving ' + formatMoney(saved);
        } else {
          savingEl.textContent = '';
        }
      }
    }

    async handleSubmit(e, row) {
      e.preventDefault();

      const button = row.querySelector('[data-bundles-add]');
      if (!button || button.disabled) return;

      button.disabled = true;
      button.classList.remove('is-added');

      const formData = new FormData(row);
      // Normalise quantity (strip whitespace, fallback to 1)
      const qty = parseInt(formData.get('quantity'), 10);
      formData.set('quantity', String(Number.isFinite(qty) && qty > 0 ? qty : 1));

      // Preorder selections carry the same line item properties the Essential
      // Preorder button would attach, so the app tags the order downstream.
      const campaign = this.rowCampaigns && this.rowCampaigns.get(row);
      if (campaign && campaign.id) {
        formData.set('properties[_essential_preorder_campaign_id]', campaign.id);
        const content = campaign.content || {};
        if (content.cartNoteKey && content.cartNoteText) {
          formData.set('properties[' + content.cartNoteKey + ']', content.cartNoteText);
        }
      }

      try {
        const url = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
        const res = await fetch(url, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || err.message || 'Could not add item');
        }

        await res.json();

        // Fire cart:update — the theme listens for this and will refresh the drawer.
        // Also fetch a fresh cart so our own render is in sync.
        await this.fetchCart();
        document.dispatchEvent(
          new CustomEvent(CART_UPDATE_EVENT, {
            bubbles: true,
            detail: {
              resource: this.cart,
              sourceId: 'bundles',
              data: { source: 'bundles' },
            },
          })
        );

        this.showAdded(button);
      } catch (err) {
        document.dispatchEvent(
          new CustomEvent(CART_ERROR_EVENT, {
            bubbles: true,
            detail: { sourceId: 'bundles', error: err && err.message },
          })
        );
        button.disabled = false;
      }
    }

    showAdded(button) {
      button.classList.add('is-added');
      setTimeout(() => {
        button.classList.remove('is-added');
        button.disabled = false;
      }, ADDED_DURATION);
    }
  }

  function fillTemplate(tpl, vars) {
    if (!tpl) return '';
    return tpl.replace(/\{(\w+)\}/g, (_, k) => (k in vars ? String(vars[k]) : ''));
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  if (!customElements.get('bundles-block')) {
    customElements.define('bundles-block', BundlesBlock);
  }
})();
